/** A Help Center section (/help/:sectionId): its author and every article. */
import { Link, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { HELP_CENTER } from '@/content/help';
import { sectionMeta } from '@/content/help/seo';
import HelpShell from '@/components/help/HelpShell';
import HelpSeo from '@/components/help/HelpSeo';
import HelpIcon from '@/components/help/HelpIcon';
import HelpSidebar from '@/components/help/HelpSidebar';
import AuthorByline from '@/components/help/AuthorByline';
import { ContactPanel } from './HelpHome';
import HelpNotFound from './HelpNotFound';

export default function HelpSectionPage() {
  const { sectionId } = useParams();
  const section = HELP_CENTER.getSection(sectionId);
  if (!section) return <HelpNotFound />;

  return (
    <HelpShell>
      <HelpSeo meta={sectionMeta(section)} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-10 pb-24">
        <div className="grid gap-12 lg:grid-cols-[250px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
              <HelpSidebar sectionId={section.id} />
            </div>
          </aside>
          <div className="min-w-0 max-w-3xl">
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[13px] text-white/50">
              <Link to="/help" className="hover:text-white">Help Center</Link>
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              <span className="text-white/75">{section.title}</span>
            </nav>
            <div className="mt-6 flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.06] ring-1 ring-white/10 text-[#C9D0EA]">
                <HelpIcon name={section.icon} className="h-6 w-6" />
              </span>
              <h1 className="text-[32px] sm:text-[40px] leading-tight font-semibold tracking-[-0.02em] text-white">{section.title}</h1>
            </div>
            <p className="mt-4 text-[16.5px] leading-relaxed text-white/70">{section.description}</p>
            <div className="mt-6">
              <AuthorByline author={section.authorProfile} compact />
            </div>

            <ul className="mt-10 divide-y divide-white/[0.07] border-y border-white/[0.07]">
              {section.articles.map((a) => (
                <li key={a.id}>
                  <Link to={a.path} className="group block py-5">
                    <span className="flex items-start justify-between gap-4">
                      <span className="text-[17px] font-medium text-white group-hover:text-[#C9D0EA] transition-colors">{a.title}</span>
                      <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-white/30 group-hover:text-white/70" aria-hidden />
                    </span>
                    <span className="mt-1.5 block text-[14.5px] leading-relaxed text-white/60">{a.summary}</span>
                  </Link>
                </li>
              ))}
              {section.articles.length === 0 && (
                <li className="py-6 text-[15px] text-white/60">Articles for this topic are on their way.</li>
              )}
            </ul>

            <div className="mt-14">
              <ContactPanel />
            </div>
          </div>
        </div>
      </div>
    </HelpShell>
  );
}
